import React from 'react';

import { PressedKeysContext, type PressedKeysContextValue } from '@neptune/shared/keyboard-domain';

type KeyPressListener = (isPressed: boolean) => void;
type NotifyKeyListenersParams = {
  key: string;
  isPressed: boolean;
};

const getOrCreateKeyListeners = (
  listenersByKey: Map<string, Set<KeyPressListener>>,
  key: string,
) => {
  const listeners = listenersByKey.get(key);

  if (listeners) {
    return listeners;
  }

  const nextListeners = new Set<KeyPressListener>();
  listenersByKey.set(key, nextListeners);
  return nextListeners;
};

export const PressedKeysProvider = ({ children }: { children: React.ReactNode }) => {
  const pressedKeysRef = React.useRef(new Set<string>());
  const listenersByKeyRef = React.useRef(new Map<string, Set<KeyPressListener>>());

  const notifyKeyListeners = React.useCallback(({ key, isPressed }: NotifyKeyListenersParams) => {
    const listeners = listenersByKeyRef.current.get(key);

    if (!listeners) {
      return;
    }

    Array.from(listeners).forEach((listener) => listener(isPressed));
  }, []);

  const onKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if (pressedKeysRef.current.has(event.key)) {
        return;
      }

      pressedKeysRef.current.add(event.key);
      notifyKeyListeners({ key: event.key, isPressed: true });
    },
    [notifyKeyListeners],
  );

  const onKeyUp = React.useCallback(
    (event: KeyboardEvent) => {
      if (!pressedKeysRef.current.has(event.key)) {
        return;
      }

      pressedKeysRef.current.delete(event.key);
      notifyKeyListeners({ key: event.key, isPressed: false });
    },
    [notifyKeyListeners],
  );

  const clearKeyPressedState = React.useCallback(() => {
    if (pressedKeysRef.current.size === 0) {
      return;
    }

    const keysToNotify = Array.from(pressedKeysRef.current);
    pressedKeysRef.current.clear();
    keysToNotify.forEach((key) => notifyKeyListeners({ key, isPressed: false }));
  }, [notifyKeyListeners]);

  const onVisibilityChange = React.useCallback(() => {
    if (document.hidden) {
      clearKeyPressedState();
    }
  }, [clearKeyPressedState]);

  const isKeyPressed = React.useCallback((key: string) => {
    return pressedKeysRef.current.has(key);
  }, []);

  const subscribeToKey = React.useCallback((key: string, listener: KeyPressListener) => {
    const listeners = getOrCreateKeyListeners(listenersByKeyRef.current, key);

    listeners.add(listener);

    return () => {
      const currentListeners = listenersByKeyRef.current.get(key);

      if (!currentListeners) {
        return;
      }

      currentListeners.delete(listener);

      if (currentListeners.size === 0) {
        listenersByKeyRef.current.delete(key);
      }
    };
  }, []);

  const contextValue = React.useMemo<PressedKeysContextValue>(
    () => ({
      isKeyPressed,
      subscribeToKey,
    }),
    [isKeyPressed, subscribeToKey],
  );

  React.useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', clearKeyPressedState);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', clearKeyPressedState);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [clearKeyPressedState, onKeyDown, onKeyUp, onVisibilityChange]);

  return <PressedKeysContext.Provider value={contextValue}>{children}</PressedKeysContext.Provider>;
};
